import { produce } from 'immer'
import { IActivity, IDiscourseCategory, INotification } from 'core/models/Models'
import {
  NOTIFICATIONS,
  NOTIFICATIONS_COUNT,
  DISCOURSE_CATEGORIES,
  ADD_NEW_NOTIFICATION,
} from 'core/actions/NotificationActions'
import { sortedInsert } from 'utils/Helpers'

export interface INotificationsState {
  notifications: INotification[]
  unreadNotificationsCount: number
  discourseCategories: IDiscourseCategory[]
  hasMore: boolean
}

const initialState: INotificationsState = {
  notifications: [],
  discourseCategories: [],
  unreadNotificationsCount: 0,
  hasMore: false,
}

const mergeNotifications = (existingNotifications: INotification[], newNotifications: INotification[]) => {
  const notificationsById = new Map<number, INotification>()
  existingNotifications.forEach((notification) => notificationsById.set(notification.pId, notification))
  newNotifications.forEach((notification) => notificationsById.set(notification.pId, notification))

  const mergedNotifications = Array.from(notificationsById.values())
  const sortedNotifications = Array<INotification>()
  mergedNotifications.forEach((notification) =>
    sortedInsert(sortedNotifications, 'timestamp', notification, true),
  )
  return sortedNotifications
}

const createPlatformNotification = (notification) => {
  let link = ''
  if (notification.notificationSource == 'ACTION') {
    link = `/akcije/${notification.actionId}`
  } else if (
    notification.notificationSource == 'CONTEST' ||
    notification.notificationSource == 'CONTEST_APPLICATION'
  ) {
    link = `/konkursi/${notification.contestId}`
  } else if (notification.notificationSource == 'KNOWLEDGE_RESOURCE') {
    link = `/znanje/${notification.resourceId}`
  } else if (notification.notificationSource == 'LINK_RESOURCE') {
    link = notification.resourceId ? `/resursi?tab=link&link=${notification.resourceId}` : '/resursi?tab=link'
  } else if (notification.notificationSource == 'EXPERTISE_RESOURCE') {
    link = `/ekspertize/${notification.resourceId}`
  } else if (notification.notificationSource == 'TOOL_RESOURCE') {
    link = `/alatke/${notification.resourceId}`
  } else if (notification.notificationSource == 'CALENDAR') {
    link = notification.resourceId ? `/kalendar?event=${notification.resourceId}` : '/kalendar'
  } else if (notification.notificationSource == 'USER') {
    link = `/profil-clanice/${notification.userId}`
  } else if (notification.notificationSource == 'COMMENT' || notification.notificationSource == 'MENTION') {
    if (notification.contestId) link = `/konkursi/${notification.contestId}`
    else if (notification.actionId) link = `/akcije/${notification.actionId}`
    else if (notification.resourceId) link = '/resursi'
  }
  const authorName = notification?.organizationAuthor
    ? `Organizacija ${notification.organizationAuthor.name}`
    : notification?.userAuthor?.name ?? ''
  const authorImageUrl = notification?.organizationAuthor
    ? notification.organizationAuthor?.imageUrl
    : notification?.userAuthor?.imageUrl
  return {
    notificationSource: notification.notificationSource,
    pId: notification.pId,
    authorImageUrl: authorImageUrl,
    authorName: authorName,
    relativeUrl: link,
    text: notification.text,
    read: notification.read,
    timestamp: notification.timestamp,
  }
}

const createDiscourseNotification = (notification) => {
  return {
    pId: notification.pId,
    authorImageUrl: notification?.userAuthor?.imageUrl,
    authorName: notification?.userAuthor?.name ?? '',
    absoluteUrl: notification.url,
    text: notification.text,
    read: notification.read,
    timestamp: notification.timestamp,
  }
}

const notificationReducer = (state = initialState, action: IActivity): INotificationsState =>
  produce(state, (draft) => {
    const { type, payload } = action
    switch (type) {
      case NOTIFICATIONS.GET.SUCCESS: {
        const allNotifications = Array<INotification>()
        const platformNotifications = payload.platformNotifications.map((notification) => {
          return createPlatformNotification(notification)
        })
        platformNotifications.forEach((element) => sortedInsert(allNotifications, 'timestamp', element, true))
        const discourseNotifications = payload.discourseNotifications.map((notification) => {
          return createDiscourseNotification(notification)
        })
        discourseNotifications.forEach((element) =>
          sortedInsert(allNotifications, 'timestamp', element, true),
        )
        draft.notifications = payload.append
          ? mergeNotifications(state.notifications, allNotifications)
          : allNotifications
        draft.unreadNotificationsCount = parseInt(payload?.unreadNotificationsCount) || 0
        draft.hasMore = Boolean(payload?.hasMore)
        break
      }
      case NOTIFICATIONS.POST.SUCCESS:
        draft.unreadNotificationsCount = 0
        draft.notifications = state.notifications.map((notification) => ({
          ...notification,
          read: true,
        }))
        break
      case NOTIFICATIONS_COUNT.GET.SUCCESS:
        draft.unreadNotificationsCount = parseInt(payload?.unreadNotificationsCount) || 0
        break
      case DISCOURSE_CATEGORIES.GET.SUCCESS:
        draft.discourseCategories = payload || []
        break
      case DISCOURSE_CATEGORIES.POST.SUCCESS:
        draft.discourseCategories = payload || []
        break
      case ADD_NEW_NOTIFICATION:
        if (payload.notificationSource == 'FORUM') {
          draft.notifications.unshift(createDiscourseNotification(payload))
        } else {
          draft.notifications.unshift(createPlatformNotification(payload))
        }
        draft.unreadNotificationsCount = state.unreadNotificationsCount + 1
        break
      default:
        return draft
    }
  })

export default notificationReducer
